import { useState } from "react";
import { Mail, Phone, CalendarHeart } from "lucide-react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "@/lib/images";

const CATEGORIES = ["All", "General", "Matching", "Booking & Visits", "Safety"];

const FAQS = [
  {
    category: "General",
    q: "What exactly is companion care?",
    a: "Companion care is non-medical support centred on friendship and daily living — conversation, games, outings, transportation to appointments, light housekeeping and laundry, help with shopping and meals, and gentle medication or exercise reminders. We do not provide nursing or medical procedures. Think of us as a trusted friend who also lends a helpful hand.",
  },
  {
    category: "General",
    q: "Where do you provide services?",
    a: "Anywhere your loved one lives in Calgary and surrounding areas — their own home, assisted living residences, retirement communities, hospitals, rehab centres, or care facilities. We come to them.",
  },
  {
    category: "Matching",
    q: "How do you match a companion with my loved one?",
    a: "It starts with a free consultation where we learn about their personality, interests, routine and preferences. We then hand-pick a companion whose character and hobbies genuinely fit — a chess lover gets a chess player, a gardener gets green thumbs.",
  },
  {
    category: "Matching",
    q: "Can we meet the companion before committing?",
    a: "Absolutely. We arrange a relaxed introduction visit so everyone can make sure it feels right. If the fit isn't perfect, we happily try another match — no hard feelings, ever.",
  },
  {
    category: "Matching",
    q: "Will it be the same companion every visit?",
    a: "Yes. Consistency builds trust and real friendship, so your loved one sees the same familiar face visit after visit.",
  },
  {
    category: "Booking & Visits",
    q: "How much does companionship cost?",
    a: "Pricing depends on visit length and frequency, and we keep it flexible and transparent. Your free consultation includes a clear, no-obligation quote tailored to your family's needs.",
  },
  {
    category: "Booking & Visits",
    q: "How quickly can visits begin?",
    a: "In most cases we can arrange a consultation within a few days and begin visits the same week a match is confirmed.",
  },
  {
    category: "Booking & Visits",
    q: "Do you offer respite for family caregivers?",
    a: "Yes — many families book regular weekly visits so they can rest, work or recharge, knowing their loved one is enjoying warm, trusted company. It's also a wonderful solution for family who live far away.",
  },
  {
    category: "Safety",
    q: "Are your companions background checked?",
    a: "Every companion completes a criminal background check, personal and professional reference verification, and our own compassion interview before ever meeting a client. We are fully insured and have a public business profile with the Better Business Bureau.",
  },
  {
    category: "Safety",
    q: "My parent has early-stage dementia. Can you still help?",
    a: "We often support seniors living with mild memory changes through patient, routine-based companionship. During your consultation we'll honestly assess whether our non-medical service is the right fit, and we're happy to suggest medical care providers when more support is needed.",
  },
];

export default function FAQ() {
  const [category, setCategory] = useState("All");
  const visible = category === "All" ? FAQS : FAQS.filter((f) => f.category === category);

  return (
    <div data-testid="faq-page">
      <section className="mx-auto max-w-4xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <p className="eyebrow mb-5" data-testid="faq-eyebrow">Questions, Answered</p>
        <h1 className="font-serif text-4xl font-bold leading-[1.1] tracking-tight text-espresso sm:text-5xl lg:text-6xl" data-testid="faq-heading">
          <MaskedLine delay={0.1}>Everything families</MaskedLine>
          <MaskedLine delay={0.25}>
            usually <em className="text-brand">ask us.</em>
          </MaskedLine>
        </h1>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
        <FadeIn delay={0.3}>
          <div className="mb-10 flex flex-wrap gap-2.5" role="tablist" aria-label="FAQ categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={category === c}
                data-testid={`faq-category-${c.toLowerCase().replace(/\s+&\s+|\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`rounded-full border-2 px-5 py-2.5 text-base font-semibold transition-all duration-200 ${
                  category === c
                    ? "border-brand bg-brand text-white shadow-md shadow-brand/25"
                    : "border-espresso/15 bg-white text-espresso hover:border-brand hover:text-brand"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </FadeIn>

        <Reveal>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {visible.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                data-testid={`faq-accordion-item-${i + 1}`}
                className="rounded-2xl border border-espresso/10 bg-white px-6 shadow-sm transition-shadow duration-300 data-[state=open]:shadow-lg data-[state=open]:shadow-espresso/10"
              >
                <AccordionTrigger className="py-5 text-left font-serif text-lg font-medium text-espresso hover:text-brand hover:no-underline sm:text-xl">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-base leading-relaxed text-warmgray sm:text-lg">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>

        <FadeIn delay={0.15} className="mt-14">
          <div className="rounded-[2rem] bg-espresso p-8 text-center sm:p-12" data-testid="faq-contact-banner">
            <h2 className="mb-3 font-serif text-2xl font-semibold text-cream sm:text-3xl">
              Still have questions?
            </h2>
            <p className="mx-auto mb-8 max-w-md text-lg text-cream/75">
              We love a good chat. Call or email us — a real person always answers.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a href={PHONE_HREF} data-testid="faq-phone-button" className="btn-primary !bg-gold !text-espresso hover:!bg-brand hover:!text-white">
                <Phone className="h-5 w-5" /> {PHONE}
              </a>
              <a href={EMAIL_HREF} data-testid="faq-email-button" className="btn-outline !border-cream/30 !text-cream hover:!border-gold hover:!text-gold">
                <Mail className="h-5 w-5" /> Email Us
              </a>
              <Link to="/contact" data-testid="faq-book-link" className="btn-outline !border-cream/30 !text-cream hover:!border-gold hover:!text-gold">
                <CalendarHeart className="h-5 w-5" /> Book a Free Consultation
              </Link>
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
